import {
  BoosterConfigSummary,
  BoosterSheetSummary,
  BoosterSlot,
} from "modules/cards/model/set";

// --- Types ---

export interface BoosterBreakdown {
  sharedSlots: BoosterSlot[];
  variationDiffs: VariationDiff[];
  totalCardsPerPack: number;
  sharedCardCount: number;
}

export interface VariationDiff {
  index: number;
  probability: number;
  uniqueSlots: BoosterSlot[];
  description: string;
}

// --- Border colors for variation blocks (static complete class names for Tailwind JIT) ---

export const VARIATION_BORDER_COLORS = [
  "border-l-primary",
  "border-l-secondary",
  "border-l-accent",
  "border-l-success",
  "border-l-warning",
  "border-l-error",
  "border-l-info",
  "border-l-neutral",
];

// --- Shared formatting helpers ---

export function formatBoosterName(name: string): string {
  return name
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (c) => c.toUpperCase())
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatSheetName(name: string): string {
  return name
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (c) => c.toUpperCase());
}

// --- Core algorithm ---

export function computeBoosterBreakdown(
  config: BoosterConfigSummary
): BoosterBreakdown {
  const { variations, sheets } = config;

  if (variations.length <= 1) {
    const slots = variations[0]?.slots ?? [];
    const total = slots.reduce((sum, s) => sum + s.count, 0);
    return {
      sharedSlots: slots,
      variationDiffs: [],
      totalCardsPerPack: total,
      sharedCardCount: total,
    };
  }

  // Build map: sheetName → array of counts per variation
  const sheetCounts = new Map<string, number[]>();
  for (let vi = 0; vi < variations.length; vi++) {
    for (const slot of variations[vi].slots) {
      let counts = sheetCounts.get(slot.sheetName);
      if (!counts) {
        counts = new Array<number>(variations.length).fill(0);
        sheetCounts.set(slot.sheetName, counts);
      }
      counts[vi] = slot.count;
    }
  }

  // Shared = sheets where all variation counts are identical and non-zero
  const sharedSheetNames = new Set<string>();
  for (const [sheetName, counts] of sheetCounts) {
    const first = counts[0];
    if (first > 0 && counts.every((c) => c === first)) {
      sharedSheetNames.add(sheetName);
    }
  }

  const sharedSlots: BoosterSlot[] = [];
  // Use the first variation's slot order for shared slots
  for (const slot of variations[0].slots) {
    if (sharedSheetNames.has(slot.sheetName)) {
      sharedSlots.push(slot);
    }
  }

  const sharedCardCount = sharedSlots.reduce((sum, s) => sum + s.count, 0);
  const totalCardsPerPack = variations[0].slots.reduce(
    (sum, s) => sum + s.count,
    0
  );

  const variationDiffs: VariationDiff[] = variations.map((variation, i) => {
    const uniqueSlots = variation.slots.filter(
      (s) => !sharedSheetNames.has(s.sheetName)
    );
    return {
      index: i,
      probability: variation.probability,
      uniqueSlots,
      description: generateVariationDescription(uniqueSlots, sheets),
    };
  });

  return { sharedSlots, variationDiffs, totalCardsPerPack, sharedCardCount };
}

// --- Description generator ---

function generateVariationDescription(
  uniqueSlots: BoosterSlot[],
  sheets: Record<string, BoosterSheetSummary>
): string {
  if (uniqueSlots.length === 0) {
    return "Same contents as other variations.";
  }

  const parts = uniqueSlots.map((slot) => describeSlot(slot, sheets));

  if (parts.length === 1) {
    return `Includes ${parts[0]}.`;
  }

  return `Includes ${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}.`;
}

function describeSlot(
  slot: BoosterSlot,
  sheets: Record<string, BoosterSheetSummary>
): string {
  const sheet = sheets[slot.sheetName];
  const name = formatSheetName(slot.sheetName);
  const cardLabel = slot.count === 1 ? "card" : "cards";

  if (!sheet) {
    return `${slot.count} ${name} ${cardLabel}`;
  }

  const tags: string[] = [];
  if (sheet.foil) tags.push("foil");
  if (sheet.fixed) tags.push("fixed");
  const tagSuffix = tags.length > 0 ? ` (${tags.join(", ")})` : "";

  if (sheet.fixed) {
    return `${slot.count} fixed ${name} ${cardLabel}${sheet.foil ? " (foil)" : ""}`;
  }

  return `${slot.count} ${name} ${cardLabel}${tagSuffix} from a pool of ${sheet.cardCount}`;
}
